/**
 * Register Command - Project Registration
 *
 * Registers a project directory in the project index.
 * Creates a ProjectEntry with UUID, path, and timestamps.
 */

import type { Command } from 'commander';
import { ProjectService } from '../../lib/services/index.js';
import { handleCLIError, ExitCodes } from '../output/error.js';
import { createServices } from '../utils/service-factory.js';
import { colors } from '../theme/index.js';
import fs from 'fs-extra';
import path from 'path';

/**
 * Options for register command.
 */
export interface RegisterOptions {
  /** Optional template to assign after registration */
  template?: string;
}

/**
 * Register register command with Commander program.
 */
export function registerRegisterCommand(program: Command): void {
  program
    .command('register <path>')
    .description('Register a project directory')
    .option('-t, --template <name>', 'assign a template after registration')
    .action(async (projectPath: string, options: RegisterOptions) => {
      try {
        await executeRegister(projectPath, options);
      } catch (error) {
        handleCLIError(error);
      }
    });
}

/**
 * Execute register operation.
 *
 * @param projectPath - Path to project directory
 * @param options - Register options
 */
export async function executeRegister(
  projectPath: string,
  options: RegisterOptions
): Promise<void> {
  const { projectIndex, appState, projectService } = createServices();

  // Expand ~ to home directory
  const expandedPath = projectPath.startsWith('~')
    ? (() => {
        const home = process.env.HOME;
        if (!home) {
          console.error(colors.danger('Cannot expand ~: HOME environment variable is not set.'));
          process.exit(ExitCodes.MISUSE);
        }
        return path.join(home, projectPath.slice(1));
      })()
    : path.resolve(projectPath);

  // Check if path exists
  if (!await fs.pathExists(expandedPath)) {
    console.error(colors.danger(`Path does not exist: ${expandedPath}`));
    throw new Error(`Path does not exist: ${expandedPath}`);
  }

  // Check for .claude directory (required)
  const claudeDir = path.join(expandedPath, '.claude');
  if (!await fs.pathExists(claudeDir)) {
    console.error(colors.danger(`No .claude/ directory found at ${expandedPath}`));
    console.error(colors.muted('Only directories with .claude/ can be registered.'));
    console.error(colors.muted('Initialize Claude Code in this project first.'));
    process.exit(ExitCodes.MISUSE);
  }

  // Check for settings files (warn if missing)
  const hasSettings = await fs.pathExists(path.join(claudeDir, 'settings.json')) ||
    await fs.pathExists(path.join(claudeDir, 'settings.local.json'));

  if (!hasSettings) {
    console.log(colors.warning(`Warning: No .claude/settings.json or settings.local.json found at ${expandedPath}`));
    console.log(colors.muted('Project will be registered, but may need Claude Code config initialization.'));
  }

  // Register the project
  const entry = await projectService.registerProject(expandedPath);

  console.log(colors.success(`Project registered successfully!`));
  console.log(colors.foreground(`  Name: ${entry.name}`));
  console.log(colors.foreground(`  ID: ${entry.id}`));
  console.log(colors.foreground(`  Path: ${entry.path}`));

  // Assign template if specified
  if (options.template) {
    console.log(colors.muted(`Assigning template: ${options.template}`));
    // Template assignment would require TemplateService integration
    // For now, just update the activeConfig field
    await projectIndex.update(entry.id, { activeConfig: options.template });
    console.log(colors.success(`Template "${options.template}" assigned.`));
  }
}